import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Search, FileText, GitBranch, ShieldCheck, BarChart3,
  UserCircle2, Inbox, FolderArchive, Star, Settings, LogOut,
  Bell, Menu, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Sheet, SheetContent } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useEffect } from "react";

const BUYER_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/discover", label: "Discover Vendors", icon: Search },
  { to: "/rfq", label: "RFQ Management", icon: FileText },
  { to: "/onboarding", label: "Onboarding", icon: GitBranch },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/analytics", label: "Spend Analytics", icon: BarChart3 },
];

const VENDOR_LINKS = [
  { to: "/vendor-dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/profile/edit", label: "Profile Editor", icon: UserCircle2 },
  { to: "/rfq/inbox", label: "RFQ Inbox", icon: Inbox },
  { to: "/documents", label: "Document Vault", icon: FolderArchive },
  { to: "/performance", label: "Performance", icon: Star },
];

function SidebarContent({ role, onClick }) {
  const links = role === "vendor" ? VENDOR_LINKS : BUYER_LINKS;
  return (
    <div className="flex flex-col h-full bg-[#0F172A] text-zinc-300 sidebar-scroll overflow-y-auto" data-testid="sidebar-content">
      <div className="px-6 py-5 border-b border-[#1E293B] flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#6366F1] grid place-items-center text-white font-bold font-outfit">V</div>
        <div>
          <div className="font-outfit font-bold text-white text-lg leading-none">VendorHub</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{role === "vendor" ? "Vendor Portal" : "Buyer Console"}</div>
        </div>
      </div>
      <nav className="flex-1 py-4">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onClick}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                  isActive ? "border-[#6366F1] bg-[#1E293B]/50 text-white" : "border-transparent hover:bg-[#1E293B]/30 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-6 py-4 border-t border-[#1E293B]">
        <NavLink to="/settings" onClick={onClick} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white py-1.5" data-testid="nav-settings">
          <Settings className="w-4 h-4" /> Settings
        </NavLink>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async () => {
    try {
      await api.put("/notifications/read");
      setNotifs(notifs.map((n) => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) markRead(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 hover:bg-zinc-100 rounded-md" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-zinc-600" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 text-[10px] font-semibold bg-[#EF4444] text-white rounded-full grid place-items-center" data-testid="notification-count">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b font-semibold">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {notifs.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">No notifications yet</div>
          )}
          {notifs.map((n) => (
            <div key={n.id} className="px-4 py-3 border-b last:border-0 hover:bg-zinc-50" data-testid="notification-item">
              <div className="font-medium text-sm">{n.title}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{n.body}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (!q) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const vendors = results?.vendors || [];
  const rfqs = results?.rfqs || [];

  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
      <Input
        placeholder="Search vendors, RFQs..."
        className="pl-9 h-9 bg-zinc-50 border-zinc-200"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="global-search-input"
      />
      {results && (vendors.length > 0 || rfqs.length > 0) && (
        <div className="absolute top-11 left-0 right-0 bg-white border border-zinc-200 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          {vendors.length > 0 && (
            <div className="p-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1">Vendors</div>
              {vendors.map((v) => (
                <button key={v.id} onClick={() => { navigate(`/vendor/${v.id}`); setQ(""); }} className="w-full text-left px-2 py-1.5 hover:bg-zinc-100 rounded text-sm" data-testid="search-result-vendor">
                  <div className="font-medium">{v.name}</div>
                  <div className="text-xs text-zinc-500">{v.category} · {v.location}</div>
                </button>
              ))}
            </div>
          )}
          {rfqs.length > 0 && (
            <div className="p-2 border-t">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1">RFQs</div>
              {rfqs.map((r) => (
                <button key={r.id} onClick={() => { navigate(user?.role === "vendor" ? "/rfq/inbox" : "/rfq"); setQ(""); }} className="w-full text-left px-2 py-1.5 hover:bg-zinc-100 rounded text-sm">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-zinc-500">{r.status}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  if (!user) return null;
  const role = user.role;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 fixed inset-y-0 left-0 z-30">
        <SidebarContent role={role} />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-[#0F172A] border-none">
          <SidebarContent role={role} onClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 h-14 flex items-center px-4 lg:px-8 gap-4">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-zinc-100 rounded-md px-2 py-1.5" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] text-white grid place-items-center text-sm font-semibold">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium leading-none">{user.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{user.company || user.role}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-zinc-400 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user.name}</div>
                  <div className="text-xs text-zinc-500 font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
